import React, { HTMLAttributes, useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import RemoveCircleOutlineRoundedIcon from "@material-ui/icons/RemoveCircleOutlineRounded";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import ErrorBoundary from "components/ErrorBoundary";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import Loader from "components/Loader";
import TableCellWrapper from "components/VetExamsTable/TableCellWrapper";
import { IExamsTableRowData } from "components/VetExamsTable/types";
import { useComplicatedRolesAndStatuses } from "hooks/useComplicatedRolesAndStatuses";
import ConfirmPopUp from "./UnAssignConfirmPopUp";

import useStyles from "./styles";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  onConfirm: () => void;
  rowData: IExamsTableRowData;
  isUnAssignSaving?: boolean;
}

const UnAssignExamCell = (props: IProps) => {
  const { rowData, onConfirm, isUnAssignSaving = false, ...restProps } = props;
  const { examAssignedUser, examStatus } = rowData;

  const classes = useStyles();
  const { isLocked, pullLocker } = useExamLockerContext();

  const { isCurrentUserChiefOrSeniorVet, isCurrentUserAssignedTrackVet } =
    useComplicatedRolesAndStatuses({
      assignedUserId: examAssignedUser?.id
    });

  const isUnAssignEnabled =
    (isCurrentUserChiefOrSeniorVet || isCurrentUserAssignedTrackVet) &&
    examStatus !== racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_IN_PROGRESS &&
    !isLocked;

  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);

  const handleOpenConfirmPopup = async () => {
    const locker = await pullLocker();
    if (locker.data.isLocked) return;

    setIsConfirmPopupOpen(true);
  };

  const handleCloseConfirmPopup = () => {
    setIsConfirmPopupOpen(false);
  };

  const handleConfirm = () => {
    setIsConfirmPopupOpen(false);
    onConfirm();
  };

  const renderCellContent = () => {
    if (!examAssignedUser?.id) return;

    if (isUnAssignSaving) {
      return <Loader size={14} disableGutters={true} />;
    }

    return (
      <IconButton
        className={classes.unassign}
        size={"small"}
        disabled={!isUnAssignEnabled}
        onClick={handleOpenConfirmPopup}
      >
        <RemoveCircleOutlineRoundedIcon fontSize={"small"} />
      </IconButton>
    );
  };

  return (
    <TableCellWrapper {...restProps} centered>
      {renderCellContent()}

      {isConfirmPopupOpen && (
        <ErrorBoundary>
          <ConfirmPopUp
            title="CONFIRM UN-ASSIGN?"
            content="The horse exam will be placed back to un-assigned after confirming."
            onClose={handleCloseConfirmPopup}
            onConfirm={handleConfirm}
            rowData={rowData}
          />
        </ErrorBoundary>
      )}
    </TableCellWrapper>
  );
};

export default React.memo(UnAssignExamCell);
